import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import {
  fertigationDecoderFields,
  hasDecoderLink,
  storedDecoderOrUndefined,
} from '@/utils/decoderLink'
import InputLabelField from '@/components/form-control/InputLabelField'
import Select from '@/components/form-control/Select'
import { useAuthContext } from '@/features/auth/context'
import { useConfigUI } from '@/features/config/context'
import useAddFertigation from '@/features/config/hooks/fertigation/useAddFertigation'
import useDetailFertigation from '@/features/config/hooks/fertigation/useDetailFertigation'
import useUpdateFertigation from '@/features/config/hooks/fertigation/useUpdateFertigation'
import { NotifyService } from '@/helpers'
import useTranslation from '@/hooks/useTranslation'
import { useUnits } from '@/hooks/useUnits'
import { DATA_PAGINATION } from '@/types'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import { Button, CircularProgress, Container, Fade, Stack, Typography } from '@mui/material'
import { useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import useUser from '@features/auth/hooks/useUser'
import { OutputDecoderType } from '@/types/decoder'

interface FertigationViewProps {
  id?: string
  name?: string
}


const FertigationView = ({ id, name }: FertigationViewProps) => {
  const { t } = useTranslation()
  const { closeSidebar } = useConfigUI()
  const { interfaceId } = useAuthContext()
  const { convertToMetric, convertLabelUnit, convertToMetricV2, convertFromMetricV2 } = useUnits()
  const { data: user } = useUser()
  const isMetric = !user?.measure?.id || user?.measure?.id === 1
  const { mutate: add, isLoading: loadingAdd } = useAddFertigation()

  const { mutate: edit, isLoading: loadingEdit } = useUpdateFertigation()
  const { data: dataItem, isFetching } = useDetailFertigation(
    Number(interfaceId),
    Number(id),
  )

  const inputRefName = useRef<HTMLInputElement>(null)
  const schema = yup
    .object({
      id: yup.mixed().nullable().optional(),
      name: yup.string().required(`${t('fertigationName')} ${t('commonFormsValidationIsRequired')}`),
      controller: yup
        .mixed()
        .required(`${t('commonWordControlValve')} ${t('commonFormsValidationIsRequired')}`)
        .test(
          'has-decoder',
          `${t('commonWordControlValve')} ${t('commonFormsValidationIsRequired')}`,
          (value) => hasDecoderLink(value as any),
        ),
      capacity: yup
        .number()
        .typeError(`${t('formsLabelsCapacity')} ${t('commonFormsValidationMustBeANumber')}`)
        .required(`${t('formsLabelsCapacity')} ${t('commonFormsValidationIsRequired')}`)
        .min(0, `${t('formsLabelsCapacity')} ${t('commonFormsValidationCannotBeNegative')}`),
    })
    .required()

  const mode = id ? 'edit' : 'create'

  const isLoading = loadingAdd || loadingEdit


  const handleSave = (data: any) => {
    // Convert capacity to metric before API submission
    const capacityMetric = isMetric
      ? data.capacity
      : convertToMetricV2(data.capacity, 'l/m', 'gpm', 2)

    if (mode === 'create') {
      add({
        interfaceId: Number(interfaceId),
        name: data.name,
        ...fertigationDecoderFields(data.controller),
        capacity: capacityMetric,
      })
    }
    if (mode === 'edit') {
      edit({
        id: Number(id),
        interfaceId: Number(interfaceId),
        name: data.name,
        ...fertigationDecoderFields(data.controller),
        capacity: capacityMetric,
      })
    }
  }

  const getOptions =
    () => async (PageIndex?: number, PageSize?: number, Keyword?: string) => {
      let data: DATA_PAGINATION | undefined
      data = await controllerDataConfigApi
        .getAll({
          interfaceId: Number(interfaceId),
          PageIndex,
          PageSize: PageSize || 20,
          Keyword,
          isRequiredStation: true,
          Purpose: [OutputDecoderType.FERTIGATION],
        })
        .then((res) => res.data)
        .catch((e) => {
          NotifyService.error(e)
          return undefined
        })

      return data
    }

  useEffect(() => {
    if (inputRefName.current) {
      inputRefName.current.focus()
    }
  }, [])

  const form = useForm({
    resolver: yupResolver(schema),
  })

  const convertedCapacity = useMemo(() => {
    if (!dataItem?.capacity) return 0
    return convertFromMetricV2(dataItem.capacity, 'l/m', 'gpm', 2)
  }, [dataItem?.capacity, convertFromMetricV2])

  useEffect(() => {
    if (dataItem) {
      form.reset({
        name: dataItem.name || '',
        controller: storedDecoderOrUndefined({
          id: dataItem.decoderId || dataItem.controller?.decoderId,
          address: dataItem.controller?.address,
          index: dataItem.controller?.id,
          name: dataItem.controller?.name,
        }),
        capacity: convertedCapacity,
        id,
      })
    }
  }, [dataItem, form, convertedCapacity, id])

  return (
    <Container sx={{ maxWidth: '450px', height: '100%' }}>
      {isFetching && (
        <Stack
          width="377px"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      )}
      {!isFetching && (
        <Fade in={!isFetching}>
          <form onSubmit={form.handleSubmit(handleSave)}>
            <Stack sx={{ padding: '34px 18px' }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '377px' }}
                marginBottom={2}
              >
                <Typography
                  sx={{
                    fontWeight: '500',
                    lineHeight: '14px',
                    color: 'text.main',
                  }}
                >
                  {id ? 'Edit Fertigation' : 'Add Fertigation'}
                </Typography>
                <Stack direction="row">
                  <Button
                    variant="secondary"
                    sx={{ marginRight: '7px', width: '85px', height: '26px' }}
                    onClick={() => closeSidebar()}
                  >
                    Cancel
                  </Button>

                  <LoadingButton
                    loading={isLoading}
                    variant="primary"
                    size="small"
                    sx={{ minWidth: '85px', height: '26px' }}
                    type="submit"
                  >
                    Save
                  </LoadingButton>
                </Stack>
              </Stack>
              {id && (
                <InputLabelField
                  name="id"
                  form={form}
                  label="ID"
                  style={{
                    height: 50,
                    borderRadius: '4px',
                  }}
                  padding="8px 16px"
                  disabled
                />
              )}

              <InputLabelField
                name="name"
                form={form}
                label="Fertigation Name"
                placeholder="Description"
                inputRef={inputRefName}
                style={{
                  height: 50,
                  borderRadius: '4px',
                  marginTop: '8px',
                }}
                padding="8px 16px"
                isRequired
              />

              <Select
                required
                minWidth={'100%'}
                isHasMore
                filterFunc={false}
                label="Control Valve"
                name="controller"
                bindLabel="name"
                bindKey="name"
                placeholder="Select one"
                form={form}
                getOptions={getOptions()}
              />

              <InputLabelField
                name="capacity"
                form={form}
                label={`${t('formsLabelsFertigationPipeCapacity')} (l/m)*`}
                placeholder={`${t('formsLabelsFertigationPipeCapacity')} (l/m)`}
                inputRef={inputRefName}
                style={{
                  height: 50,
                  borderRadius: '4px',
                  marginTop: '8px',
                }}
                padding="8px 16px"
                isRequired
              />
            </Stack>
          </form>
        </Fade>
      )}
    </Container>
  )
}

export default FertigationView
